import React from 'react';
import { Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { pillBaseSx } from '../utils/leadPillStyles';
import { formatWaitingDuration } from '../utils/leadWaitingDuration';
import { getServedByName } from '../utils/leadServedBy';

const REFRESH_INTERVAL_MS = 60000;

const STATUS_VARIANTS = {
  in_progress: { color: 'success', labelKey: 'leads.qualifiedLead_inProgress' },
  closed: { color: 'grey', labelKey: 'leads.qualifiedLead_closed' },
  expired: { color: 'grey', labelKey: 'leads.qualifiedLead_expired' },
};

// Uma única pill de status: fica laranja (alerta de SLA) enquanto o lead
// espera um humano, mostrando há quanto tempo — substitui o Alert separado
// que existia antes. Sem waitingForHuman, cai no status normal do lead;
// 'open' sem espera não mostra nada, pois a pill de "atendido por" (Agente
// Beworks) já comunica que é a IA quem está atendendo. Pelo mesmo motivo,
// "em atendimento" também não aparece quando já há um atendente humano
// nomeado ao lado — a pill "Atendido por" já diz isso.
const LeadStatusChip = ({
  status,
  waitingForHuman,
  waitingForHumanSince,
  isAssignedToMe,
  assignedToName,
  lastAssignedToName,
  fullWidth,
}) => {
  const { t } = useTranslation();
  const [, forceRefresh] = React.useState(0);

  React.useEffect(() => {
    if (!waitingForHuman || !waitingForHumanSince) return undefined;
    const id = setInterval(() => forceRefresh((n) => n + 1), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [waitingForHuman, waitingForHumanSince]);

  if (waitingForHuman) {
    const duration = waitingForHumanSince ? formatWaitingDuration(waitingForHumanSince) : null;
    const label = duration ? `${t('leads.waitingForHuman')} · ${duration}` : t('leads.waitingForHuman');

    return (
      <Chip
        label={label}
        size="small"
        sx={(theme) => ({
          ...pillBaseSx,
          bgcolor: theme.alpha((theme.vars || theme).palette.warning.main, 0.12),
          color: (theme.vars || theme).palette.warning.dark,
          ...(fullWidth && { width: '100%' }),
        })}
      />
    );
  }

  const variant = STATUS_VARIANTS[status];
  if (!variant) return null;

  const hasHumanAttendant = isAssignedToMe || Boolean(getServedByName({ assignedToName, lastAssignedToName, status }));
  if (status === 'in_progress' && hasHumanAttendant) return null;

  return (
    <Chip
      label={t(variant.labelKey)}
      size="small"
      sx={(theme) => ({
        ...pillBaseSx,
        ...(variant.color === 'grey'
          ? { bgcolor: 'action.hover', color: 'text.primary' }
          : {
              bgcolor: theme.alpha((theme.vars || theme).palette[variant.color].main, 0.12),
              color: (theme.vars || theme).palette[variant.color].dark,
              // O tom escuro some sobre o fundo escuro; no dark o texto usa o claro.
              ...theme.applyStyles('dark', { color: (theme.vars || theme).palette[variant.color].light }),
            }),
      })}
    />
  );
};

export default LeadStatusChip;
